import { FC } from 'react';

import {
    ChevronDown,
    ChevronUp,
    Package,
    Save,
    Store,
    Truck,
} from 'lucide-react';

import { NovaPoshtaCitySelect } from '../../components/nova_poshta/NovaPoshtaCitySelect';
import { Button } from '../../components/ui/Button';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '../../components/ui/Card';
import { ShippingMethod } from '../../types';

interface Props {
    isOpen: boolean;
    onToggle: () => void;
    availableMethods: ShippingMethod[];
    selectedMethods: ShippingMethod[];
    onChange: (methods: ShippingMethod[]) => void;
    onSave: () => void;
    processing: boolean;
    error?: string;
    cityError?: string;
    city: string;
    cityRef: string;
    onCityChange: (city: string, cityRef: string) => void;
}

const METHOD_CONFIG: Record<
    ShippingMethod,
    { label: string; description: string; icon: typeof Truck }
> = {
    nova_poshta: {
        label: 'Нова Пошта (Відділення)',
        description: 'Відділення або поштомат',
        icon: Truck,
    },
    ukrposhta: {
        label: 'Укрпошта',
        description: 'Доставка у поштове відділення',
        icon: Package,
    },
    pickup: {
        label: 'Самовивіз',
        description: 'Самовивіз від продавця',
        icon: Store,
    },
};

const METHOD_ORDER: ShippingMethod[] = ['nova_poshta', 'ukrposhta', 'pickup'];

export const ShippingMethodsForm: FC<Props> = ({
    isOpen,
    onToggle,
    availableMethods,
    selectedMethods,
    onChange,
    onSave,
    processing,
    error,
    cityError,
    city,
    cityRef,
    onCityChange,
}) => {
    const methods = METHOD_ORDER.filter((method) =>
        availableMethods.includes(method),
    ).map((method) => ({
        value: method,
        ...METHOD_CONFIG[method],
    }));
    const pickupEnabled = selectedMethods.includes('pickup');
    const handleToggleMethod = (method: ShippingMethod) => {
        if (!availableMethods.includes(method)) return;

        if (selectedMethods.includes(method)) {
            onChange(selectedMethods.filter((m) => m !== method));
            return;
        }

        onChange([...selectedMethods, method]);
    };

    return (
        <Card className="h-fit" data-testid="shipping-methods-form">
            <CardHeader
                className="cursor-pointer transition-colors hover:bg-muted/50"
                onClick={onToggle}
            >
                <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Truck className="h-5 w-5 text-primary" />
                        Методи доставки
                    </div>
                    {isOpen ? (
                        <ChevronUp className="h-4 w-4 text-muted-foreground" />
                    ) : (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    )}
                </CardTitle>
                <CardDescription>
                    Налаштуйте, які методи доставки доступні покупцям
                </CardDescription>
            </CardHeader>

            {isOpen && (
                <CardContent className="space-y-4">
                    <div className="space-y-2">
                        {methods.map((method) => {
                            const Icon = method.icon;
                            const checked = selectedMethods.includes(
                                method.value,
                            );

                            return (
                                <label
                                    key={method.value}
                                    className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 ${checked ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'}`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() =>
                                            handleToggleMethod(method.value)
                                        }
                                        className="sr-only"
                                    />
                                    <div
                                        className={`flex h-7 w-7 items-center justify-center rounded-full ${checked ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'}`}
                                    >
                                        <Icon className="h-4 w-4" />
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium">
                                            {method.label}
                                        </p>
                                        <p className="text-xs text-muted-foreground">
                                            {method.description}
                                        </p>
                                    </div>
                                </label>
                            );
                        })}
                    </div>

                    {error && <p className="text-sm text-red-500">{error}</p>}

                    {pickupEnabled && (
                        <div className="rounded-xl border border-border p-3">
                            <NovaPoshtaCitySelect
                                id="pickup_city"
                                value={city}
                                required
                                initialFallback={Boolean(city && !cityRef)}
                                onSelect={(selectedCity) =>
                                    onCityChange(
                                        selectedCity.name,
                                        selectedCity.ref,
                                    )
                                }
                                onFallbackInput={(value) =>
                                    onCityChange(value, '')
                                }
                                onSelectionClear={(value) =>
                                    onCityChange(value, '')
                                }
                                error={cityError}
                            />
                            <p className="mt-1 text-xs text-muted-foreground">
                                Місто буде показане покупцям як місце
                                самовивозу.
                            </p>
                        </div>
                    )}

                    <Button
                        type="button"
                        onClick={onSave}
                        disabled={processing}
                        className="w-full"
                    >
                        <Save className="mr-2 h-4 w-4" />
                        {processing
                            ? 'Збереження...'
                            : 'Зберегти методи доставки'}
                    </Button>
                </CardContent>
            )}
        </Card>
    );
};
